import { LvTime, LvTimePayload } from 'Interfaces/ReferenceInterfaces/LvTimeInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { lvTimeServices } from 'services/ReferenceScreenServices/lvTimeServices';

export class LvTimeStore {
  lvTimes: LvTime[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedLvTime: LvTime | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchLvTimes = async (page = 1, pageSize = 10000) => {
    this.isLoading = true;
    try {
      const response = await lvTimeServices.getAllLvTimes(page, pageSize);
      runInAction(() => {
        this.lvTimes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching LV times');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollLvTimes = async (page = 1, pageSize = 10000) => {
    this.isPolling = true;
    try {
      const response = await lvTimeServices.getAllLvTimes(page, pageSize);
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.lvTimes) !== JSON.stringify(response.data)) {
          this.lvTimes = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if (this.environment === 'development') {
        console.error('Error polling LV times:', error);
      }
      toast.error('Error polling LV times!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 10000) => {
    await this.fetchLvTimes(page, pageSize);
  };

  createLvTime = async (lvTimeData: LvTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await lvTimeServices.createLvTime(lvTimeData);
      if (response.status) {
        toast.success('LV time created successfully');
        await this.fetchLvTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create LV time');
        return false;
      }
    } catch (error) {
      toast.error('Error creating LV time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateLvTime = async (id: number, lvTimeData: LvTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await lvTimeServices.updateLvTime(id, lvTimeData);
      if (response.status) {
        toast.success('LV time updated successfully');
        await this.fetchLvTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update LV time');
        return false;
      }
    } catch (error) {
      toast.error('Error updating LV time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteLvTime = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await lvTimeServices.deleteLvTime(id);
      if (response.status) {
        toast.success('LV time deleted successfully');

        const isLastItemOnPage = this.lvTimes.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchLvTimes(this.pagination.currentPage - 1);
        } else {
          await this.fetchLvTimes(this.pagination.currentPage);
        }
      } else {
        toast.error(response.message || 'Failed to delete LV time');
      }
    } catch (error) {
      toast.error('Error deleting LV time');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedLvTime = (lvTime: LvTime | null) => {
    this.selectedLvTime = lvTime;
  };
}

export const lvTimeStore = new LvTimeStore();
